import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  InputGroup
} from 'react-bootstrap';
import {
  FaLock,
  FaEye,
  FaEyeSlash,
  FaCheck,
  FaTimes,
  FaKey,
  FaUserShield,
  FaArrowLeft
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import AdminNavbar from './components/AdminNavbar';
import { authAPI } from '../../services/api';

const ChangePassword = () => {
  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check authentication
    const currentUser = authAPI.getCurrentUser();
    console.log('Current user:', currentUser);
    if (!currentUser) {
      navigate('/admin/login');
      return;
    }
    setUser(currentUser);
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log(`Field ${name} changed:`, value);
    setFormData({
      ...formData,
      [name]: value
    });

    // Clear error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }

    // Check password strength for new password
    if (name === 'new_password') {
      checkPasswordStrength(value);
    }
  };

  const checkPasswordStrength = (password) => {
    setPasswordStrength({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    });
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword({
      ...showPassword,
      [field]: !showPassword[field]
    });
  };

  const validateForm = () => {
    const newErrors = {};
    console.log('Validating form data:', formData);

    if (!formData.current_password.trim()) {
      newErrors.current_password = 'Current password is required';
    }

    if (!formData.new_password.trim()) {
      newErrors.new_password = 'New password is required';
    } else if (formData.new_password.length < 8) {
      newErrors.new_password = 'Password must be at least 8 characters';
    }

    if (!formData.confirm_password.trim()) {
      newErrors.confirm_password = 'Please confirm your password';
    } else if (formData.new_password !== formData.confirm_password) {
      newErrors.confirm_password = 'Passwords do not match';
    }

    console.log('Validation errors:', newErrors);
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submitted');
    
    // Clear previous messages
    setSuccess(false);
    setSuccessMessage('');
    setErrors({});
    
    // Validate form
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      console.log('Form validation failed');
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    console.log('Calling changePassword API with:', {
      current_password: '[HIDDEN]',
      new_password: '[HIDDEN]',
      confirm_password: '[HIDDEN]'
    });

    try {
      const result = await authAPI.changePassword({
        current_password: formData.current_password,
        new_password: formData.new_password,
        confirm_password: formData.confirm_password
      });

      console.log('API Response:', result);

      if (result && result.success === true) {
        console.log('Password change successful');
        setSuccess(true);
        setSuccessMessage(result.message || 'Password changed successfully!');
        
        // Clear form
        setFormData({
          current_password: '',
          new_password: '',
          confirm_password: ''
        });
        
        // Reset password strength
        setPasswordStrength({
          length: false,
          uppercase: false,
          lowercase: false,
          number: false,
          special: false
        });
        
        // Auto hide success message after 8 seconds
        setTimeout(() => {
          console.log('Hiding success message');
          setSuccess(false);
          setSuccessMessage('');
        }, 8000);
        
      } else {
        console.log('Password change failed:', result);
        // Handle backend validation errors
        if (result && result.errors && Array.isArray(result.errors)) {
          const backendErrors = {};
          result.errors.forEach(error => {
            if (error.toLowerCase().includes('current')) {
              backendErrors.current_password = error;
            } else if (error.toLowerCase().includes('new')) {
              backendErrors.new_password = error;
            } else if (error.toLowerCase().includes('confirm')) {
              backendErrors.confirm_password = error;
            } else {
              backendErrors.submit = error;
            }
          });
          console.log('Backend errors:', backendErrors);
          setErrors(backendErrors);
        } else if (result && result.message) {
          console.log('Setting submit error:', result.message);
          setErrors({ submit: result.message });
        } else {
          console.log('Setting generic error');
          setErrors({ submit: 'Failed to change password. Please try again.' });
        }
      }
    } catch (error) {
      console.error('Error changing password:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        request: error.request
      });
      
      let errorMessage = 'Failed to change password. Please try again.';
      
      if (error.response) {
        // Backend error
        console.error('Response error:', error.response);
        if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.status === 401) {
          errorMessage = 'Session expired. Please login again.';
          setTimeout(() => navigate('/admin/login'), 2000);
        } else if (error.response.status === 400) {
          errorMessage = 'Invalid request. Please check your input.';
        } else if (error.response.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        }
      } else if (error.request) {
        // Network error
        console.error('Network error:', error.request);
        errorMessage = 'Network error. Please check your internet connection.';
      } else {
        // API request setup error
        console.error('Request setup error:', error.message);
        errorMessage = error.message || 'Failed to change password.';
      }
      
      console.log('Setting error message:', errorMessage);
      setErrors({ submit: errorMessage });
    } finally {
      setLoading(false);
      console.log('Request completed');
    }
  };

  const calculateStrengthScore = () => {
    const requirements = Object.values(passwordStrength);
    const metRequirements = requirements.filter(Boolean).length;
    return (metRequirements / requirements.length) * 100;
  };

  const getStrengthColor = () => {
    const score = calculateStrengthScore();
    if (score < 40) return 'danger';
    if (score < 80) return 'warning';
    return 'success';
  };

  const getStrengthText = () => {
    const score = calculateStrengthScore();
    if (score < 40) return 'Weak';
    if (score < 80) return 'Medium';
    return 'Strong';
  };

  const handleLogout = () => {
    authAPI.logout();
    navigate('/admin/login');
  };

  if (!user) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Checking authentication...</p>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <AdminNavbar onLogout={handleLogout} user={user} />
      
      <Container fluid className="py-4 admin-content">
        {/* Header Section */}
        <Row className="mb-4 align-items-center">
          <Col>
            <h2 className="fw-bold mb-0">
              <FaKey className="me-2 text-primary" />
              Change Password
            </h2>
            <p className="text-muted mb-0">Update your account password securely</p>
          </Col>
          <Col className="text-end">
            {/* <Button 
              variant="outline-secondary" 
              onClick={() => navigate('/admin/dashboard')}
              disabled={loading}
            >
              <FaArrowLeft className="me-2" />
              Back to Dashboard
            </Button> */}
          </Col>
        </Row>

        {/* Debug Info (remove in production) */}
        <div className="mb-3">
          <small className="text-muted">
            Debug: User: {user?.username} | Loading: {loading.toString()} | Success: {success.toString()} | Errors: {JSON.stringify(errors)}
          </small>
        </div>

        {/* Success Message */}
        {success && successMessage && (
          <Alert 
            variant="success" 
            className="mb-3" 
            dismissible 
            onClose={() => {
              setSuccess(false);
              setSuccessMessage('');
            }}
          >
            <div className="d-flex align-items-center">
              <FaCheck className="me-2" />
              <div>
                <strong>Success!</strong>
                <div>{successMessage}</div>
              </div>
            </div>
          </Alert>
        )}
        
        {/* Error Message */}
        {errors.submit && (
          <Alert 
            variant="danger" 
            className="mb-3" 
            dismissible 
            onClose={() => setErrors({...errors, submit: ''})}
          >
            <div className="d-flex align-items-center">
              <FaTimes className="me-2" />
              <div>
                <strong>Error!</strong>
                <div>{errors.submit}</div>
              </div>
            </div>
          </Alert>
        )}

        {/* Main Content */}
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card className="border-0 shadow-lg">
              <Card.Header className="bg-primary text-white py-4 border-0">
                <div className="d-flex align-items-center">
                  <FaUserShield size={32} className="me-3" />
                  <div>
                    <h2 className="mb-1">Change Password</h2>
                    <p className="mb-0 opacity-75">Update your account password</p>
                  </div>
                </div>
              </Card.Header>
              
              <Card.Body className="p-4">
                <Form onSubmit={handleSubmit} noValidate>
                  {/* Current Password */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">
                      <FaLock className="me-2" />
                      Current Password
                    </Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showPassword.current ? "text" : "password"}
                        name="current_password"
                        value={formData.current_password}
                        onChange={handleChange}
                        placeholder="Enter current password"
                        isInvalid={!!errors.current_password}
                        required
                        disabled={loading}
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={() => togglePasswordVisibility('current')}
                        disabled={loading}
                      >
                        {showPassword.current ? <FaEyeSlash /> : <FaEye />}
                      </Button>
                      <Form.Control.Feedback type="invalid">
                        {errors.current_password}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>
                  
                  {/* New Password */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">
                      <FaKey className="me-2" />
                      New Password
                    </Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showPassword.new ? "text" : "password"}
                        name="new_password"
                        value={formData.new_password}
                        onChange={handleChange}
                        placeholder="Enter new password"
                        isInvalid={!!errors.new_password}
                        required
                        disabled={loading}
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={() => togglePasswordVisibility('new')}
                        disabled={loading}
                      >
                        {showPassword.new ? <FaEyeSlash /> : <FaEye />}
                      </Button>
                      <Form.Control.Feedback type="invalid">
                        {errors.new_password}
                      </Form.Control.Feedback>
                    </InputGroup>
                    
                    {/* Password Strength Indicator */}
                    {formData.new_password && (
                      <div className="mt-3">
                        <div className="d-flex justify-content-between mb-2">
                          <small>Password Strength:</small>
                          <small className={`text-${getStrengthColor()} fw-bold`}>
                            {getStrengthText()}
                          </small>
                        </div>
                        <div className="progress mb-3" style={{ height: '5px' }}>
                          <div
                            className={`progress-bar bg-${getStrengthColor()}`}
                            role="progressbar"
                            style={{ width: `${calculateStrengthScore()}%` }}
                            aria-valuenow={calculateStrengthScore()}
                            aria-valuemin="0"
                            aria-valuemax="100"
                          ></div>
                        </div>
                        
                        <div className="row">
                          <div className="col-6">
                            <small className={`d-flex align-items-center ${passwordStrength.length ? 'text-success' : 'text-muted'}`}>
                              {passwordStrength.length ? <FaCheck className="me-1" /> : <FaTimes className="me-1" />}
                              8+ characters
                            </small>
                            <small className={`d-flex align-items-center ${passwordStrength.uppercase ? 'text-success' : 'text-muted'}`}>
                              {passwordStrength.uppercase ? <FaCheck className="me-1" /> : <FaTimes className="me-1" />}
                              Uppercase letter
                            </small>
                          </div>
                          <div className="col-6">
                            <small className={`d-flex align-items-center ${passwordStrength.lowercase ? 'text-success' : 'text-muted'}`}>
                              {passwordStrength.lowercase ? <FaCheck className="me-1" /> : <FaTimes className="me-1" />}
                              Lowercase letter
                            </small>
                            <small className={`d-flex align-items-center ${passwordStrength.number ? 'text-success' : 'text-muted'}`}>
                              {passwordStrength.number ? <FaCheck className="me-1" /> : <FaTimes className="me-1" />}
                              Number
                            </small>
                          </div>
                        </div>
                      </div>
                    )}
                  </Form.Group>
                  
                  {/* Confirm Password */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">
                      <FaKey className="me-2" />
                      Confirm New Password
                    </Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showPassword.confirm ? "text" : "password"}
                        name="confirm_password"
                        value={formData.confirm_password}
                        onChange={handleChange}
                        placeholder="Confirm new password"
                        isInvalid={!!errors.confirm_password}
                        required
                        disabled={loading}
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={() => togglePasswordVisibility('confirm')}
                        disabled={loading}
                      >
                        {showPassword.confirm ? <FaEyeSlash /> : <FaEye />}
                      </Button>
                      <Form.Control.Feedback type="invalid">
                        {errors.confirm_password}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>
                  
                  {/* Submit Button */}
                  <div className="d-grid gap-2">
                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      disabled={loading}
                      className="py-3"
                    >
                      {loading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Changing Password...
                        </>
                      ) : (
                        <>
                          <FaKey className="me-2" />
                          Change Password
                        </>
                      )}
                    </Button>
                    
                    <Button
                      variant="outline-secondary"
                      onClick={() => navigate('/admin/dashboard')}
                      disabled={loading}
                    >
                      <FaArrowLeft className="me-2" />
                      Cancel
                    </Button>
                  </div>
                </Form>
              </Card.Body>
              
              <Card.Footer className="bg-light border-0 py-3">
                <div className="text-center">
                  <small className="text-muted">
                    <FaKey className="me-1" />
                    For security reasons, please choose a strong password and don't share it with anyone.
                  </small>
                </div>
              </Card.Footer>
            </Card>
            
            {/* Security Tips */}
            <Card className="border-0 shadow-sm mt-4">
              <Card.Body>
                <h5 className="mb-3">
                  <FaUserShield className="me-2" />
                  Password Security Tips
                </h5>
                <ul className="list-unstyled mb-0">
                  <li className="mb-2">
                    <FaCheck className="text-success me-2" />
                    Use at least 8 characters with a mix of letters, numbers, and symbols
                  </li>
                  <li className="mb-2">
                    <FaCheck className="text-success me-2" />
                    Avoid using personal information like birthdates or names
                  </li>
                  <li className="mb-2">
                    <FaCheck className="text-success me-2" />
                    Don't reuse passwords from other accounts
                  </li>
                  <li className="mb-0">
                    <FaCheck className="text-success me-2" />
                    Consider using a password manager
                  </li>
                </ul>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ChangePassword;